import { MatSnackBar } from "@angular/material/snack-bar";
import { environment } from "./environments/environment";
import { Router } from "@angular/router";
import { MatDialog } from "@angular/material/dialog";
import { LoadingComponent } from "./app/components/dialogs/loading/loading.component";
import { SnackBarColor } from "node_js/models/enums/snackbar-color";
import { PagesEnum } from "node_js/models/enums/pages";
import { DialogAlertConfirmComponent } from "./app/components/dialogs/dialog-alert-confirm/dialog-alert-confirm.component";
import { Buffer } from "buffer";
import { ComponentDataType } from "node_js/models/enums/component-data-type";
import { RangeType } from "node_js/models/enums/range-type";

export const smallScreenSize = 600;

let uiTextTranslator: ((text: string) => string) | null = null;

export function setUiTextTranslator(translator: (text: string) => string) {
  uiTextTranslator = translator;
}

export function translateUiText(text: string) {
  if (!text || typeof text !== 'string' || !uiTextTranslator) {
    return text;
  }

  const translatedText = uiTextTranslator(text);
  return translatedText || text;
}


export const connLink = (environment.apiUrl || "").trim();

export const transitionSpeed: number = 400;

export function generateId(length?: number) {

  if (length == null) {
    length = 10;
  }

  let result = generateString(length);
  result = "temp_id_" + result;
  return result;
}

export function checkRange(componentDataType: ComponentDataType, rangeType: RangeType, value: string, min: string, max: string) {

  if (rangeType == RangeType.CHANGES) {
    return true
  }
  else if (rangeType != RangeType.NONE) {
    //If data type is string and comp value == min
    if (componentDataType == ComponentDataType.STRING && rangeType == RangeType.EQUAL && value == min) {
      return true
    }
    else if (componentDataType == ComponentDataType.NUMBER) {
      let valueNum = Number(value);

      if (isNaN(valueNum)) {
        return false;
      }

      let minNum = null;
      let maxNum = null;

      if (min != null && min != "") {
        minNum = Number(min)
      }
      if (max != null && max != "") {
        maxNum = Number(max)
      }

      if (
        ((minNum == null || isNaN(minNum) == true) || valueNum >= minNum)
        && ((maxNum == null || isNaN(maxNum) == true) || valueNum <= maxNum)) {
        console.log("TRUE", minNum, maxNum, valueNum)
        return true;

      }
    }
  }
  return false;
}


export async function mergeBufferArray(bufferArray: { type: string; data: number[] }[]): Promise<Uint8Array> {
  if (!Array.isArray(bufferArray) || bufferArray.length === 0) {
    throw new Error("Invalid buffer array received");
  }

  // Convert buffer objects to Uint8Arrays
  const uint8Arrays = bufferArray.map(bufObj => new Uint8Array(bufObj.data));

  // Calculate total size and merge all buffers
  const totalSize = uint8Arrays.reduce((sum, buf) => sum + buf.length, 0);
  const mergedBuffer = new Uint8Array(totalSize);

  let offset = 0;
  uint8Arrays.forEach(buf => {
    mergedBuffer.set(buf, offset);
    offset += buf.length;
  });

  return mergedBuffer;
}

export function bufferObjectToArrayBuffer(bufferObject: { type: string; data: number[] }): ArrayBuffer {
  const uint8Array = new Uint8Array(bufferObject.data);
  const mimeType = uint8Array[0] === 0x89 && uint8Array[1] === 0x50 ? "image/png" : "image/jpeg";
  return uint8Array.buffer;
}

export async function processImage(file: File, fileName: string, matDialog: MatDialog, matSnackBar: MatSnackBar): Promise<File> {
  return new Promise(async (resolve, reject) => {
    let loading = presentLoading(matDialog, "Compressing Image");

    try {
      // If it's an SVG, return it as-is without compression
      if (file.type === 'image/svg+xml' || file.name.toLowerCase().endsWith('.svg')) {
        loading.close();
        resolve(file);
        return;
      }

      let maxMB = 1048576 * 15;
      if (file.size > maxMB) {
        presentSnackBar("Large files could take up to 30 seconds to compress. It isn't frozen!", SnackBarColor.DANGER, matSnackBar);
      }

      let blob: Blob = await compressImage(file, 50, 150);
      const newFileName = `${fileName}.jpg`;
      const compressedFile = new File([blob], newFileName, { type: 'image/jpeg' });
      loading.close();

      resolve(compressedFile);
    } catch (error) {
      loading.close();

      reject(error);
    }
  });
}

export async function compressImage(
  file: File,
  minSizeKB: number = 50,
  maxSizeKB: number = 150,
  minWidth: number = 350,
  minHeight: number = 250
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = URL.createObjectURL(file);
    img.onload = () => {
      let width = img.width;
      let height = img.height;
      // Only shrink if larger than min
      if (width > minWidth || height > minHeight) {
        const widthRatio = minWidth / width;
        const heightRatio = minHeight / height;
        const scale = Math.max(widthRatio, heightRatio, 1); // Don't upscale
        width = Math.floor(width * scale);
        height = Math.floor(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        reject(new Error("Canvas context error"));
        return;
      }
      ctx.drawImage(img, 0, 0, width, height);

      let quality = 0.8;
      let attempts = 0;
      const maxAttempts = 10;
      const minQuality = 0.4;

      function tryCompress() {
        canvas.toBlob(
          (blob) => {
            if (!blob) {
              reject(new Error("Compression failed"));
              return;
            }
            const sizeKB = blob.size / 1024;
            if ((sizeKB <= maxSizeKB && sizeKB >= minSizeKB) || attempts >= maxAttempts || quality < minQuality) {
              resolve(blob);
            } else {
              quality -= 0.1;
              attempts++;
              tryCompress();
            }
          },
          "image/jpeg",
          quality
        );
      }
      tryCompress();
    };
    img.onerror = () => reject(new Error("Image loading error"));
  });
}

export function arrayBufferToBlob(buffer: ArrayBuffer, mimeType: string = "application/octet-stream"): Blob {
  return new Blob([buffer], { type: mimeType });
}

export function presentAlertConfirm(header: string, message: string, dialog: MatDialog) {
  return new Promise<boolean>(async (resolve, reject) => {
    let dialogRef = dialog.open(DialogAlertConfirmComponent, {
      width: '300px',
      data: { title: translateUiText(header), message: translateUiText(message) }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (!result) {
        result = false;
      }
      console.log("RESULT", result);
      resolve(result);
    })

  })

}


export async function presentSnackBar(message: string, snackbarColor: SnackBarColor, snackBar: MatSnackBar, closeMessage?: string, duration?: number) {
  if (!duration) {
    duration = 5000;
  }
  await snackBar.open(translateUiText(message), translateUiText(closeMessage), {
    panelClass: [snackbarColor],
    duration
  });

  return;
}
export function changePage(url: PagesEnum, router: Router) {
  router.navigate([url]);
}

export function presentLoading(dialog: MatDialog, message?: string) {
  if (!message) {
    message = "Loading...";
  }
  let dialogRef = presentDialog(dialog, LoadingComponent, { message: translateUiText(message) }, true, "175px", "80px");
  return dialogRef;
}


export function presentDialog(dialog: MatDialog, component: any, data: any, disableClose: boolean = false, width: string = "93vw", height: string = "97vh") {

  let dialogRef = dialog.open(component, {
    height,
    width,
    data: data,
    maxWidth: "100vw",
    disableClose: disableClose
  });
  return dialogRef;
}


export function convertLocalDateToUTCMySQLString(date: Date) {
  console.log(date)
  let utc: string = date.toISOString().slice(0, 19).replace('T', ' ');
  return utc;
}

function parseUTCDateString(dateString: string): Date {
  if (!dateString) {
    return new Date(NaN);
  }

  const normalized = String(dateString).trim();

  if (/([zZ]|[+-]\d{2}:?\d{2})$/.test(normalized)) {
    const parsedWithZone = new Date(normalized);
    if (!isNaN(parsedWithZone.getTime())) {
      return parsedWithZone;
    }
  }

  const match = normalized.match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2})(?:\.(\d{1,3}))?)?)?$/);
  if (match) {
    const [, year, month, day, hour = '0', minute = '0', second = '0', millisecond = '0'] = match;
    return new Date(Date.UTC(
      Number(year),
      Number(month) - 1,
      Number(day),
      Number(hour),
      Number(minute),
      Number(second),
      Number(millisecond.padEnd(3, '0'))
    ));
  }

  const isoFallback = new Date(normalized.replace(' ', 'T') + 'Z');
  if (!isNaN(isoFallback.getTime())) {
    return isoFallback;
  }

  return new Date(NaN);
}

//Converts our utcString (mysql) into a date object
export function convertUTCDateStringToUTCDate(dateString: string) {
  return parseUTCDateString(dateString);
}

export function getNumberSuffix(num: number) {
  let j = num % 10,
    k = num % 100;
  if (j === 1 && k !== 11) {
    return num + "st";
  }
  if (j === 2 && k !== 12) {
    return num + "nd";
  }
  if (j === 3 && k !== 13) {
    return num + "rd";
  }
  return num + "th";
}

export function copyToClipboard(text: string, matSnackBar: MatSnackBar) {
  const selBox = document.createElement('textarea');
  selBox.style.position = 'fixed';
  selBox.style.left = '0';
  selBox.style.top = '0';
  selBox.style.opacity = '0';
  selBox.value = text;
  document.body.appendChild(selBox);
  selBox.focus();
  selBox.select();
  document.execCommand('copy');
  document.body.removeChild(selBox);

  presentSnackBar("Copied to clipboard", SnackBarColor.SUCCESS, matSnackBar)
}

//NOTE: IF DATE SENT IN IS NOT IN UTC, YOU WILL GET AN INCORRECT LOCAL DATE
export function convertUTCStringToLocalDate(utcString: string) {
  return parseUTCDateString(utcString);
}


export function getFormattedDateStringFromLocalDate(date: Date) {

  let timeOfDayText = "";
  let hours = date.getHours();
  console.log("hours", hours)
  if (hours > 12) {
    hours = hours - 12;
    console.log("hours is now", hours)
    timeOfDayText = "PM";
  }
  else {
    timeOfDayText = "AM";
  }
  let minutes = date.getMinutes().toString();
  if (Number(minutes) < 10) {
    minutes = "0" + minutes;
  }

  let datePart = [
    date.getMonth() + 1,
    date.getDate(),
    date.getFullYear()
  ].map((n, i) => n.toString().padStart(i === 2 ? 4 : 2, "0")).join("/");
  let timePart = [
    hours,
    minutes
  ].map((n, i) => n.toString()).join(":");

  timePart += timeOfDayText;
  return datePart + " " + timePart;
}

export function getCurrentDateForDB() {
  return new Date().toISOString().slice(0, 19).replace('T', ' ');
}



export function generateString(length?: number) {

  if (length == null) {
    length = 10;
  }

  var result = '';
  var characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
  var charactersLength = characters.length;
  for (var i = 0; i < length; i++) {
    result += characters.charAt(Math.floor(Math.random() *
      charactersLength));
  }
  return result;
}

export function updateObjKeepingRef(sourceObj: any, newObj: any): void {
  if (!sourceObj || !newObj || sourceObj === newObj) return;

  if (Array.isArray(sourceObj) && Array.isArray(newObj)) {
    if (sourceObj.length > newObj.length) {
      sourceObj.splice(newObj.length);
    }
    newObj.forEach((item, index) => {
      if (index < sourceObj.length) {
        if (item && typeof item === 'object' && sourceObj[index] && !(item instanceof Date)) {
          updateObjKeepingRef(sourceObj[index], item);
        } else {
          sourceObj[index] = item;
        }
      } else {
        sourceObj.push(item);
      }
    });
    return;
  }

  // Remove stale keys
  Object.keys(sourceObj).forEach(key => {
    if (!(key in newObj)) {
      delete sourceObj[key];
    }
  });

  // Update/Add keys
  Object.keys(newObj).forEach(key => {
    if (newObj[key] && typeof newObj[key] === 'object' && sourceObj[key] && !(newObj[key] instanceof Date)) {
      updateObjKeepingRef(sourceObj[key], newObj[key]);
    } else {
      sourceObj[key] = newObj[key];
    }
  });
}

export function getTimeBetweenTwoDates(pastDate: Date, futureDate: Date, longMessage: boolean) {

  var secondBetweenTwoDate = Math.abs((futureDate.getTime() - pastDate.getTime()) / 1000);

  let minutes = Math.floor(secondBetweenTwoDate / 60);

  let hours = Math.floor(minutes / 60); // => 4 => the times 3 fits into 13  

  let days = Math.floor(hours / 24);

  let lastMessageTime = "";
  if (Math.floor(minutes) == 0) {
    lastMessageTime = "Now";
  }
  else if (minutes < 60) {
    lastMessageTime = minutes + (longMessage == true ? " minute" : " min") + (minutes == 1 ? "" : "s") + (longMessage == true ? " ago" : "");
  }
  else if (hours < 24) {
    lastMessageTime = hours + (hours == 1 ? " hour" : " hours") + (longMessage == true ? " ago" : "");
  }
  else {
    lastMessageTime = days + (days == 1 ? " day" : " days") + (longMessage == true ? " ago" : "");
  }

  return lastMessageTime;
}